import { Button, Modal } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type GoogleDriveItem, getAttachmentLimitsOptions, type SharePointItem } from "@/api";
import { Text } from "@/components/Text";
import { loadDriveDoc, loadSharePointDoc } from "./cloudFiles";
import { OneDriveBrowser } from "./cloudPicker/OneDriveBrowser";
import { SharePointBrowser } from "./cloudPicker/SharePointBrowser";
import { attachableCloudFile } from "./folderFiles";
import { GoogleDrivePicker } from "./GoogleDrivePicker";
import type { CloudFile } from "./useAttachments";

export type CloudSource = "oneDrive" | "sharepoint" | "googleDrive";

const TITLE = { oneDrive: "oneDriveTitle", sharepoint: "sharepointTitle", googleDrive: "driveTitle" } as const;

/** Pick OneDrive, SharePoint or Google Drive files to attach to the next message. */
export function CloudFilesDialog({
  source,
  onClose,
  onAttach,
}: Readonly<{ source: CloudSource | null; onClose: () => void; onAttach: (files: CloudFile[]) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const { data: limits } = useQuery({
    ...getAttachmentLimitsOptions(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: source !== null,
  });
  const [picked, setPicked] = useState<SharePointItem[]>([]);
  const [driveFiles, setDriveFiles] = useState<CloudFile[]>([]);
  const close = () => {
    setPicked([]);
    setDriveFiles([]);
    onClose();
  };

  const files: CloudFile[] =
    source === "googleDrive"
      ? driveFiles
      : picked.map((item) => ({ name: item.name, mimeType: item.mime_type, load: () => loadSharePointDoc(item) }));
  const pickDrive = (items: GoogleDriveItem[]) =>
    setDriveFiles(items.map((item) => ({ name: item.name, mimeType: item.mime_type, load: () => loadDriveDoc(item) })));
  const selection = {
    selected: picked,
    onToggle: (item: SharePointItem, on: boolean) =>
      setPicked((current) => (on ? [...current, item] : current.filter((f) => f.id !== item.id))),
    usable: (item: SharePointItem) => attachableCloudFile(item, limits),
  };

  return (
    <Modal
      open={source !== null}
      onClose={close}
      title={source ? t(TITLE[source]) : undefined}
      closeLabel={t("close")}
      actions={
        <>
          {files.length > 0 && (
            <Text variant="caption" tone="secondary" className="mr-auto">
              {t("selected", { count: files.length })}
            </Text>
          )}
          <Button variant="secondary" onClick={close}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={files.length === 0}
            onClick={() => {
              onAttach(files);
              close();
            }}
          >
            {t("attach", { count: files.length })}
          </Button>
        </>
      }
    >
      {source === "oneDrive" && <OneDriveBrowser selection={selection} />}
      {source === "sharepoint" && <SharePointBrowser selection={selection} />}
      {source === "googleDrive" && <GoogleDrivePicker onFiles={pickDrive} />}
    </Modal>
  );
}
